/**
 * Утилиты для работы с маской телефона в формате +7 (XXX) XXX-XX-XX
 */

export const PHONE_MASK_PREFIX = "+7 ";

/**
 * Извлекает цифры из строки, удаляя все нецифровые символы
 */
export const getDigits = (value: string): string => value.replace(/\D/g, "");

/**
 * Форматирует цифры в маску телефона.
 * Предполагается, что digits могут начинаться с 7, 8 или 9, либо не начинаться с 7.
 * Нормализует номер к формату +7 (XXX) XXX-XX-XX.
 */
export const formatPhoneMask = (digits: string): string => {
  if (!digits) return "";

  // Нормализуем к формату, начинающемуся с 7
  const normalized = digits.startsWith("7") ? digits : `7${digits}`;
  const local = normalized.slice(1, 11); // максимум 10 цифр после 7

  let result = PHONE_MASK_PREFIX;

  if (local.length > 0) {
    result += `(${local.slice(0, 3)}`;
  }

  if (local.length >= 4) {
    result += `) ${local.slice(3, 6)}`;
  }

  if (local.length >= 7) {
    result += `-${local.slice(6, 8)}`;
  }

  if (local.length >= 9) {
    result += `-${local.slice(8, 10)}`;
  }

  return result;
};

/**
 * Применяет маску к rawValue, обрабатывая ввод цифр и символа '+'.
 * Возвращает отформатированную строку с маской.
 */
export const applyPhoneMask = (rawValue: string): string => {
  const digits = getDigits(rawValue);

  // Если digits пусты, но rawValue начинается с '+', оставляем rawValue (пользователь вводит '+')
  if (!digits) {
    return rawValue.startsWith('+') ? rawValue : '';
  }

  // Обработка номера, начинающегося с 8 (российский код)
  if (digits[0] === "8") {
    if (digits.length === 1) {
      return "8"; // частичный ввод, оставляем как есть
    }

    if (digits[1] === "9") {
      // Нормальный номер, преобразуем 8XXXXXXXXXX -> 7XXXXXXXXXX
      return formatPhoneMask(`7${digits.slice(1, 11)}`);
    }

    // Если после 8 идет не 9, это невалидная заготовка под телефон
    return rawValue;
  }

  if (digits[0] === "7") {
    return formatPhoneMask(digits.slice(0, 11));
  }

  if (digits[0] === "9") {
    return formatPhoneMask(`7${digits.slice(0, 10)}`);
  }

  // Любые другие цифры (например, начинающиеся с 1,2,3...) не форматируем
  return rawValue;
};

/**
 * Проверяет, соответствует ли строка полной маске телефона +7 (XXX) XXX-XX-XX.
 * Допускает пробелы в начале/конце.
 */
export const isValidMaskedPhone = (value: string): boolean => {
  return /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(value.trim());
};

/**
 * Нормализует телефон для отправки на сервер.
 * Преобразует маску в международный формат +7XXXXXXXXXX.
 * Поддерживает номера, начинающиеся с 8, 7, 9.
 */
export const normalizePhoneForSubmit = (value: string): string => {
  const digits = getDigits(value);

  if (digits.length === 11 && (digits.startsWith("7") || digits.startsWith("8"))) {
    return `+7${digits.slice(1)}`;
  }

  if (digits.length === 10 && digits.startsWith("9")) {
    return `+7${digits}`;
  }

  // Если не удалось нормализовать, возвращаем значение без изменений (например, email)
  return value.trim();
};

